<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：連結可能トラクターヘッドマスタ画面
 *
 * 作成者：2021/11/25 ノア）賀
 * 最終修正者：2022/1/22 ノア）黄
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.managementTargetMaster.viewTitle', [managementTargetShowName]) }}</span>
            <tti-svg-icon icon-name="wo_035"></tti-svg-icon>
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.avlTractorHeadMaster.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title-container">
        <div class="tti-content-title">
          <span class="contentTitle">{{ $t('master.avlTractorHeadMaster.contentTitle') }}</span>
          <span>{{ tableData.length }}</span>
          <span class="viewTitle">{{ $t('master.viewTitle.subject') }}</span>
          <div class="currentManagementTarget">
            <span>{{ managementTargetShowName }}</span>
            <span>:</span>
            <span>{{ currentManagementTargetNameAndCode }}</span>
          </div>
        </div>
        <div class="functionArea">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="tableData.length"
            :page-size="visablePageSize"
            :hide-on-single-page="true"
            :current-page.sync="currentPage"
            @current-change="onCurrentPageChange"
          >
          </el-pagination>
          <div class="searchBox">
            <el-input v-model.trim="searchText" onkeyup="this.value=this.value.replace(/[,]/g,'')">
              <el-button slot="append" icon="el-icon-search" @click="onSearchBoxClick"></el-button>
            </el-input>
          </div>
        </div>
      </div>
      <el-form :model="form" ref="form" :disabled="pageLock">
        <el-table
          ref="tableRef"
          :data="visableTableData"
          height="calc(100vh - 226px)"
          border
          highlight-current-row
          :stripe="true"
          header-row-class-name="tti-master-table-header"
          :row-class-name="tableRowClassName"
          :empty-text="$t('messages.common.emptyTableData')"
          @selection-change="handleSelectionChange"
          @sort-change="handleSortChange"
        >
          <el-table-column type="selection" width="35"></el-table-column>
          <!-- No -->
          <el-table-column type="index" :label="$t('master.avlTractorHeadMaster.no')" width="50" align="center">
          </el-table-column>
          <!-- メーカ -->
          <el-table-column
            class-name="required"
            prop="maker"
            :label="$t('master.avlTractorHeadMaster.maker')"
            width="500"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].maker'" :rules="rules.maker">
                <el-select
                  :id="'tableData[' + scope.$index + '].maker'"
                  v-model="scope.row.maker"
                  size="small"
                  filterable
                  @change="onMakerChange(scope.row)"
                >
                  <el-option
                    v-for="item in tractorHeadMakerList"
                    :key="item.id"
                    :label="item.maker"
                    :value="item.maker"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
          <!-- 型式 -->
          <el-table-column
            class-name="required"
            prop="tractorHeadId"
            :label="$t('master.avlTractorHeadMaster.type')"
            width="500"
            :sortable="true"
          >
            <template slot-scope="scope">
              <el-form-item :prop="'tableData[' + scope.$index + '].tractorHeadId'" :rules="rules.type">
                <el-select
                  :id="'tableData[' + scope.$index + '].tractorHeadId'"
                  v-model="scope.row.tractorHeadId"
                  size="small"
                  filterable
                  @change="onTypeChange(scope.row)"
                >
                  <el-option
                    v-for="item in getTypeList(scope.row.maker)"
                    :key="item.id"
                    :label="item.type"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </template>
          </el-table-column>
        </el-table>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
        <!-- 削除 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onDeleteButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.delete)"
          ><tti-svg-icon icon-name="su_013"></tti-svg-icon>{{ $t('buttons.delete') }}</el-button
        >
        <!-- 追加 -->
        <el-button
          class="tti-btn-default tti-btn-005"
          @click="onInsertButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.insert)"
        >
          <tti-svg-icon icon-name="su_011"></tti-svg-icon>{{ $t('buttons.insert') }}
        </el-button>
      </div>
      <div class="tti-bottom-right-area">
        <!-- 出力 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onOutputButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.output)"
        >
          <tti-svg-icon icon-name="su_003"></tti-svg-icon>{{ $t('buttons.output') }}
        </el-button>
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form, Table } from 'element-ui'
import { find, findIndex, findLastIndex } from 'lodash'
import { AxiosResponse } from 'axios'

import config from '@/config/config'
import MST_BASE, { FormItemType } from '@/views/master/MST_BASE.vue'
import { i18n } from '@/locales'
import {
  getTractorHeadMasterData,
  getAvlTractorHeadMasterData,
  updateAvlTractorHeadMasterData,
  avlTractorHeadCSVDownload
} from '@/store/apis'
import {
  TractorHeadMasterData,
  ManagementTargetMasterData,
  LockParam,
  MasterData,
  ResponseData,
  AvlTractorHeadMasterData
} from '@/models'
import HelperService from '@/service/helperService'
import { RULES, validateService } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { moveService } from '@/commons/services/moveService'

/**
 * トラクターヘッドマスタ画面
 *
 * @class
 */
@Component({
  name: 'mst-trctr-head'
})
export default class MST_AVL_TRACTOR_HEAD extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  @Ref('tableRef') readonly tableRef!: Table

  /**
   * 画面ID
   */
  pageId = 'MST_AVL_TRACTOR_HEAD'

  /**
   * 第一列の項目（Focus用）
   */
  firstField = 'maker'

  /**
   * 画面名
   */
  pageName = i18n.t('master.avlTractorHeadMaster.viewTitle').toString()

  /**
   * 管理対象
   */
  managementTarget = {} as ManagementTargetMasterData

  /**
   * トラクターヘッドマスタ
   */
  tractorHeadMaster: TractorHeadMasterData[] = []

  /**
   * トラクターヘッドマスタ
   */
  tractorHeadMakerList: TractorHeadMasterData[] = []

  /**
   * トラクターヘッド編集FORM
   */
  form = {
    table: this.tableData
  } as Record<string, AvlTractorHeadMasterData[]>

  /**
   * 管理対象
   *
   * @returns {string} 管理対象
   */
  get currentManagementTargetNameAndCode(): string {
    return `${this.managementTarget.managementTargetName}(${this.managementTarget.managementTargetCode})`
  }

  /**
   * 検証ルール
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules = {} as any

  /**
   * CSVヘッダーリスト
   */
  csvHeaders = ['maker', 'type']

  // eslint-disable-next-line max-lines-per-function
  initRule(): void {
    this.rules = {
      // メーカ
      maker: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.avlTractorHeadMaster.maker')]).toString(),
          trigger: 'change'
        }
      ],
      // 形式
      type: [
        // 必須
        {
          required: true,
          message: i18n.t('vaildate.common.required', [i18n.t('master.avlTractorHeadMaster.type')]).toString(),
          trigger: 'change'
        },
        {
          validator: validateService.getValidator([
            // 複雑さチェック
            {
              rule: RULES.CUSTOM,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              callback: (value: string, rule: any): string | undefined => {
                const currentRowIndex = validateService.getCurrentRowIndex(rule)
                const currentRow = this.visableTableData[currentRowIndex]

                const target = currentRow['tractorHeadId']
                const isError = this.checkMultipleRepeat(target)

                if (isError) {
                  const message = i18n
                    .t('vaildate.common.repeat', [
                      i18n.t('master.tractorHeadMaster.maker') + '+' + i18n.t('master.tractorHeadMaster.type')
                    ])
                    .toString()
                  return message
                }
              }
            }
          ]),
          trigger: 'change'
        }
      ]
    }
  }

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('MST_AVL_TRCTR_HEAD > mounted')
    this.initFormTemplate()
    this.initManagementTarget()
    if (!this.routeQueryCheck()) {
      return
    }
    await this.fetchTractorHeadMasterData()
    this.fetchTableData()
    this.checkPageLock()
    this.initSearchTargets()
    this.initFormRef(this.formRef)
    this.initTableRef(this.tableRef)
  }

  protected fetchTableDataBefore(): () => Promise<MasterData> {
    return () => {
      const params = {
        managementTargetId: this.managementTarget.id
      }

      return getAvlTractorHeadMasterData(params)
    }
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      this.$log.debug(`MST_BASE > ${this.pageId} > fetchTableData`, this.businessOffice)
      const data = (await this.fetchTableDataBefore()()) as MasterData
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.initTableData(data.list as Array<any>)
      await this.fetchTableDataAfter()
      return true

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e?.data?.common?.code === 'E00095') {
        moveService.beforePageChange = undefined
        this.$router.push(config.menu.subjectMaster)
        return false
      }

      this.initTableData([])
      this.fetchTableDataAfter()
      this.$log.debug(`MST_BASE > ${this.pageId} > fetchTableData: e=`, e)
      throw e
    }
  }

  fetchTableDataAfter(): void {
    this.initRule()
    this.initValidate(this.rules)
    this.multipleSelection = []
  }

  /**
   * ページロック取得APIのパラメータを取得する
   *
   * @returns {LockParam} パラメータ
   */
  getLockParam(): LockParam {
    const param = {
      pageId: this.pageId,
      manageTargetId: this.managementTarget.id
    } as LockParam

    return param
  }

  /**
   * URLから管理対象取得
   *
   * @returns {void}
   */
  initManagementTarget(): void {
    this.managementTarget = {
      id: this.$route.query.managementTargetId,
      managementTargetCode: this.$route.query.managementTargetCode,
      managementTargetName: this.$route.query.managementTargetName
    } as ManagementTargetMasterData
  }

  /**
   * パラメータ着信がない場合
   *
   * @returns {boolean} 関連結菓
   */
  routeQueryCheck(): boolean {
    if (
      !this.managementTarget.id ||
      !this.managementTarget.managementTargetCode ||
      !this.managementTarget.managementTargetName
    ) {
      msgBoxService.info(i18n.t('vaildate.common.E00092').toString())
      moveService.beforePageChange = undefined
      this.$router.push(config.menu.subjectMaster)

      return false
    }
    return true
  }

  /**
   * トラクターヘッドマスタを取得する
   *
   * @returns {void}
   */
  async fetchTractorHeadMasterData(): Promise<void> {
    try {
      const masters = await getTractorHeadMasterData()
      this.tractorHeadMaster = (masters.list as TractorHeadMasterData[]) || []
      this.setMakerList()
    } catch (e) {
      this.$log.debug('MST_AVL_TRACTOR_HEAD > fetchTractorHeadMasterData: e=', e)
      throw e
    }
  }

  /**
   * メーカリスト取得（重複データを削除する）
   *
   * @returns {void}
   */
  setMakerList(): void {
    const map = {} as Record<string, TractorHeadMasterData>
    this.tractorHeadMaster.forEach(item => {
      if (item.maker) {
        if (!map[item.maker]) {
          map[item.maker] = item
          map[item.maker].typeList = [
            {
              type: item.type
            }
          ]
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          map[item.maker].typeList.push({
            type: item.type
          })
        }
      }
    })

    this.tractorHeadMakerList = Object.values(map)
  }

  /**
   * 型式リスト取得
   *
   * @param {string} maker メーカ
   * @returns {TractorHeadMasterData[]} 型式リスト
   */
  getTypeList(maker?: string): TractorHeadMasterData[] {
    if (!maker) {
      return this.tractorHeadMaster
    }

    return this.tractorHeadMaster.filter(item => item.maker === maker)
  }

  /**
   * メーカ変更
   *
   * @param {AvlTractorHeadMasterData} row トラクターヘッド
   * @returns {void}
   */
  onMakerChange(row: AvlTractorHeadMasterData): void {
    this.$log.debug('MST_AVL_TRACTOR_HEAD > onMakerChange', row)
    if (!row.tractorHeadId) {
      return
    }

    const typeList = this.getTypeList(row.maker)
    if (findIndex(typeList, item => item.type === row.tractorHeadId) === -1) {
      row.tractorHeadId = ''
    }
  }

  /**
   * タイプ変更
   *
   * @param {AvlTractorHeadMasterData} row 管理場所
   * @returns {void}
   */
  onTypeChange(row: AvlTractorHeadMasterData): void {
    this.$log.debug('MST_AVL_TRACTOR_HEAD > onTypeChange', row)
    const tractorHead = find(this.tractorHeadMaster, item => item.id === row.tractorHeadId)
    if (tractorHead && row.maker !== tractorHead.maker) {
      row.maker = tractorHead.maker
    }
    this.$forceUpdate()
    this.formRef.validate()
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    try {
      const valid = await this.formRef.validate()
      if (!valid) {
        return false
      }
      return valid
    } catch {
      this.formRef.validate((valid, fields) => {
        if (!valid) {
          this.focusOnValidField(fields)
        }
      })
      return false
    }
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    try {
      const params = this.getUpsertRequest()
      await updateAvlTractorHeadMasterData(params)
    } catch (e) {
      this.$log.debug('MST_AVL_TRACTOR_HEAD > saveTableDataAPI: e=', e)
      const err = e as AxiosResponse<ResponseData>
      // 管理対象が存在しない場合、管理対象登録へ遷移する処理を追加
      if (err.data.common.code === 'E00095') {
        moveService.beforePageChange = undefined
        this.$router.push(config.menu.subjectMaster)
      }
      throw e
    }
  }

  /**
   * 保存パラメータを作成する
   *
   * @returns {any} 保存パラメータ
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getUpsertRequest(): any {
    const param = {
      managementTargetId: this.managementTarget.id,
      tractorHeadIdList: [] as Array<string>
    }

    param.tractorHeadIdList = this.tableData.map(item => item.tractorHeadId)

    return param
  }

  /**
   * 出力API
   *
   * @returns {void}
   */
  async tableDataOutputAPI(): Promise<void> {
    try {
      const params = {
        managementTargetId: this.managementTarget.id
      }

      const res = await avlTractorHeadCSVDownload(params)
      HelperService.download(res.fileName, res.data)
    } catch (e) {
      this.$log.debug('MST_AVL_TRACTOR_HEAD > tableDataOutputAPI: e=', e)
      const err = e as AxiosResponse<ResponseData>
      // 管理対象が存在しない場合、管理対象登録へ遷移する処理を追加
      if (err.data.common.code === 'E00095') {
        moveService.beforePageChange = undefined
        this.$router.push(config.menu.subjectMaster)
      }
      throw e
    }
  }

  /**
   * 関連重複チェック
   *
   * @param {string} value チェック対象
   * @returns {boolean} true: 関連重複がない
   */
  checkMultipleRepeat(value: string): boolean {
    let error = false
    for (let index = 0; index < this.tableData.length; index++) {
      const first = findIndex(this.tableData, (item: AvlTractorHeadMasterData) => `${item.tractorHeadId}` === value)
      const last = findLastIndex(this.tableData, (item: AvlTractorHeadMasterData) => `${item.tractorHeadId}` === value)
      if (last !== first) {
        error = true
        break
      }
    }

    return error
  }

  /**
   * 検索目標
   *
   * @returns {void}
   */
  initSearchTargets(): void {
    this.searchTargets = [
      {
        type: FormItemType.Select,
        field: 'maker',
        list: () => {
          return this.tractorHeadMakerList.map(item => {
            return { code: item.id ? item.id : '', name: item.maker ? item.maker : '' }
          })
        }
      },
      {
        type: FormItemType.Select,
        field: 'tractorHeadId',
        list: () => {
          return this.tractorHeadMaster.map(item => {
            return { code: item.id ? item.id : '', name: item.type ? item.type : '' }
          })
        }
      }
    ]
  }

  /**
   * 入力FORMのテンプレートを初期化する
   *
   * @returns {void}
   */
  initFormTemplate(): void {
    this.formTemplate = {
      maker: '',
      type: ''
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .cell {
  text-overflow: unset;
}

::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-content-title {
  width: 800px;
}

.tti-viewTitle {
  margin-right: 20px;
}

.fileInput {
  display: none;
}

.currentManagementTarget {
  display: inline-block;
  margin-left: 100px;
}
</style>
